// Widget: Meine Listen — Äquivalent zu "Meine Projekte", aber für die
// persönlichen/geteilten Aufgabenlisten (Listen-Feature).

import { useEffect, useState } from "preact/hooks";
import { Link } from "wouter-preact";
import type { PersonalTask } from "@juuplan/shared";
import { useLists } from "../../store/personalTasks";
import { api } from "../../lib/api";

export function MyListsWidget(): preact.JSX.Element {
  const { lists, loading: listsLoading } = useLists();
  const [openCounts, setOpenCounts] = useState<Map<number, number>>(new Map());

  useEffect(() => {
    let cancelled = false;
    // Owner-scoped, not-done tasks across every list — same approximation as
    // MyProjectsWidget (counts tasks I own; a shared list's tasks owned by
    // someone else but assigned to me aren't reflected here).
    void api<PersonalTask[]>("/api/personal-tasks").then((rows) => {
      if (cancelled) return;
      const counts = new Map<number, number>();
      for (const t of rows) {
        if (t.list_id === null) continue;
        counts.set(t.list_id, (counts.get(t.list_id) ?? 0) + 1);
      }
      setOpenCounts(counts);
    }, () => {
      // Absichtlich still: die Listen selbst kommen aus `useLists()` und
      // rendern unabhaengig davon. Bei einem Fehlschlag hier bleiben nur die
      // "X offen"-Badges bei 0 statt einen zweiten Ladezustand fuer eine
      // Zusatzinfo einzufuehren.
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (listsLoading)
    return <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">Lädt…</p>;
  if (lists.length === 0)
    return (
      <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
        Keine Aufgabenlisten angelegt.
      </p>
    );

  return (
    // Gleiche Begruendung wie in MyProjectsWidget: `auto-fit` statt fixer
    // Spaltenzahl, damit eine einzelne Liste nicht auf einem Drittel der
    // Kachel klebt, sondern die Reihe ausnutzt.
    <div class="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
      {lists.slice(0, 6).map((l) => (
        <Link
          key={l.id}
          href={`/lists/${l.id}`}
          class="group relative block rounded-lg bg-[color:var(--color-surface-container-lowest)] border border-[color:var(--color-outline-variant)] hover:border-[color:var(--color-outline)] hover:shadow-level-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-[border-color,box-shadow] duration-150 overflow-hidden"
        >
          <div
            class="h-1 w-full"
            style={{ background: l.color ?? "var(--color-primary)" }}
            aria-hidden="true"
          />
          <div class="p-4 flex flex-col gap-2">
            <header class="flex items-start gap-3">
              <h3 class="flex-1 text-base font-semibold text-[color:var(--color-on-surface)] tracking-tight truncate">
                {l.name}
              </h3>
              {l.scope === "space" && (
                <span class="shrink-0 inline-flex items-center rounded-md px-2 py-0.5 text-label font-semibold bg-[color:var(--color-secondary-container)] text-[color:var(--color-on-secondary-container)]">
                  Geteilt
                </span>
              )}
            </header>
            <p class="text-xs text-[color:var(--color-on-surface-variant)]">
              {openCounts.get(l.id) ?? 0} offen
            </p>
          </div>
        </Link>
      ))}
    </div>
  );
}
